/**
 * One place that knows which modals exist, so that opening any one of them closes the rest.
 *
 * THE PROBLEM THIS REPLACES. Every modal store used to hand-list its siblings and call
 * `closeModal()` on each, which meant N stores each restating the same set N-1 times. The list
 * had already drifted: `hero-image-modal-store` closed everybody, and nobody closed it, so opening
 * any other modal left the hero image dialog stacked underneath. Two of the three openers in
 * `edit-color-collections-store` had also fallen a store behind the third. Nothing failed — the
 * modals just quietly overlapped.
 *
 * WHY A REGISTRY RATHER THAN A SHARED IMPORT LIST. A module that imported all the stores, imported
 * back by all the stores, is a cycle; it happens to work today because every `getState()` sits
 * inside a closure, but it is the kind of thing that breaks on a bundler upgrade for reasons
 * nobody enjoys diagnosing. Registration inverts it: stores depend on this module and it depends
 * on none of them.
 *
 * A store that is never imported never registers — and its modal is never rendered either, so
 * there is nothing to close. `main-react.jsx` imports every modal component, which is what pulls
 * all of these in on a normal page.
 */

const stores = new Map();

/**
 * Registers a modal store. Call once, at module scope, immediately after creating the store.
 *
 * @param {string} name Unique key for this store, passed back to {@link closeOtherModals}.
 * @param {{ getState: () => { closeModal: () => void } }} store The zustand store.
 */
export function registerModalStore(name, store) {
  stores.set(name, store);
}

/**
 * Closes every registered modal except the named one. Call it at the top of an `openModal`.
 *
 * @param {string} name The store doing the opening, which must not close itself.
 */
export function closeOtherModals(name) {
  stores.forEach((store, key) => {
    if (key === name) return;

    // A store that somehow lacks closeModal is skipped rather than throwing: failing to close a
    // sibling should never stop the modal the member actually asked for from opening.
    store.getState()?.closeModal?.();
  });
}
